import React from 'react'

import {
  Alert,
  Box,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { EmployeesGetUserDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { unifiedAddEmployeeEvents } from 'modules/employees/lib/events'

import { Fields } from '../Fields/Fields'
import { WizardData, genericAmplitudeProperties } from '../types'
import { validateEmailForCreation } from '../utils'
import { groupFieldsByGroup } from '../utils'

const isCompletedStep = (data: WizardData) => {
  return !!data?.firstName && !!data?.lastName && !!data?.isValidEmail && !!data?.legalEntityId
}

const GeneralInformationStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [state, setState] = React.useState({
    isEmailFocused: false,
    isLegalEntityFocused: false,
  })

  const { data: employeesData } = useQuery(EmployeesGetUserDocument, {
    variables: {
      emails: [data?.email?.trim() || ''],
    },
    skip: !data?.email,
  })
  const employees = employeesData?.employees?.employeesConnection?.nodes || []

  const fields = data.fieldsSchema?.filter((field) => field.category === 'general_information')
  const legalEntities = fields?.find(
    (field) => field.fieldName === 'legal_entity_id'
  )?.allowedValues
  const legalEntityCountry = legalEntities?.find(
    (value) => value.key === data.legalEntityId
  )?.country
  const filterFields = fields?.filter((field) => {
    if (field.fieldName === 'employee_group_id' && !data?.legalEntityId) {
      return false
    }
    return true
  })
  const groupedFields = groupFieldsByGroup(filterFields)
  const emailValidation = validateEmailForCreation(data?.email?.trim() || '', employees)
  const validEmail = emailValidation.valid
  const maxDateBirthday = new Date()
  maxDateBirthday.setFullYear(maxDateBirthday.getFullYear() - 14)

  const getFieldError = (fieldName: string): string | undefined => {
    if (fieldName === 'email') {
      return emailValidation.valid ? undefined : emailValidation.errorString
    }
  }

  useEffect(() => {
    onDataChanged({
      ...data,
      isValidEmail: validEmail,
      legalEntityCountry: legalEntityCountry || undefined,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validEmail, legalEntityCountry])

  useOnNext(() => {
    track(unifiedAddEmployeeEvents.addGeneralInformation, genericAmplitudeProperties(data))
  })

  const onChangeField = (fieldName: string, value: string) => {
    onDataChanged({
      ...data,
      [fieldName]: value,
    })
  }

  const handleEmailOnBlur = () => {
    setState({ ...state, isEmailFocused: false })
  }

  const handleEmailOnFocus = () => {
    setState({ ...state, isEmailFocused: true })
  }

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Text.Header2>{i18n.t('add_employee_modal.general_information_step.header')}</Text.Header2>
        {Object.entries(groupedFields ?? {}).map(([group, groupFields]) => (
          <Box key={group} flexDirection="row" gap="s16">
            {groupFields.map((field) => {
              return (
                <Fields
                  key={field.fieldName}
                  field={field}
                  data={data}
                  onChangeField={onChangeField}
                  handleOnFocus={handleEmailOnFocus}
                  handleOnBlur={handleEmailOnBlur}
                  maxDate={maxDateBirthday}
                  getFieldError={getFieldError}
                />
              )
            })}
          </Box>
        ))}
      </Box>
      <Box paddingTop="s16" maxWidth="s200">
        <Box height="s140">
          {state.isEmailFocused && (
            <Alert color="note">
              {i18n.t('add_employee_modal.general_information_step.email_alert')}
            </Alert>
          )}
        </Box>
        <Box gap="s8">
          <Alert color="note">
            {i18n.t('add_employee_modal.general_information_step.legal_entity_note')}
          </Alert>
          <Alert color="info">
            ☝️ {i18n.t('add_employee_modal.general_information_step.legal_entity_info')}
          </Alert>
        </Box>
      </Box>
    </Box>
  )
}

export const generalInformationStep = (): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.general_information_step.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <GeneralInformationStep {...props} />,
  isCompleted: isCompletedStep,
})
